import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { usePagination, SearchBar } from '../components/Pagination';
import { confirmDialog } from '../utils/confirm';

const REGISTRY = 'cahier-texte';

const EMPTY = {
  date: new Date().toISOString().slice(0, 10),
  classId: '', subjectId: '', teacherId: '',
  title: '', content: '', homework: '',
  duration: 60, status: 'FAIT',
};

const STATUS = {
  FAIT:    ['green', 'Fait'],
  PARTIEL: ['gold',  'Partiel'],
  REPORTE: ['red',   'Reporté'],
};

export default function CahierTexte() {
  const { user } = useAuth();
  const isEnseignant = (user?.roles || []).includes('ENSEIGNANT');

  const [entries, setEntries]   = useState([]);
  const [classes, setClasses]   = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classFilter, setClassFilter]   = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); });
  const [dateTo, setDateTo]     = useState(new Date().toISOString().slice(0, 10));
  const [modal, setModal]       = useState(null); // null | 'new' | entry
  const [vue, setVue]           = useState(null); // entry à afficher
  const [flash, setFlash]       = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(true);

  function showFlash(msg) { setFlash(msg); setTimeout(() => setFlash(''), 3000); }

  function load() {
    setLoading(true);
    Promise.all([
      api.get(`/registry/${REGISTRY}`).catch(() => ({ records: [] })),
      api.get('/catalog/classes').catch(() => []),
      api.get('/catalog/subjects').catch(() => []),
      api.get('/catalog/teachers').catch(() => []),
    ]).then(([reg, cls, sub, tch]) => {
      const all = (Array.isArray(reg) ? reg : reg?.records || []).map((r) => ({ id: r.id, ...r.data }));
      setEntries(all.sort((a, b) => (b.date || '').localeCompare(a.date || '')));
      setClasses(cls || []);
      setSubjects(sub || []);
      setTeachers(tch || []);
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  // Filtres
  const filtered = entries.filter((e) => {
    if (classFilter   && e.classId   !== classFilter)   return false;
    if (subjectFilter && e.subjectId !== subjectFilter)  return false;
    if (e.date < dateFrom || e.date > dateTo)            return false;
    return true;
  });

  const { paginated, search, setSearch, Pagination, total, filtered: pgFiltered } = usePagination(filtered, {
    pageSize: 5,
    searchKeys: ['title', 'content', 'className', 'subjectName'],
  });

  // Stats
  const stats = {
    fait:    filtered.filter((e) => e.status === 'FAIT').length,
    partiel: filtered.filter((e) => e.status === 'PARTIEL').length,
    reporte: filtered.filter((e) => e.status === 'REPORTE').length,
  };
  const tauxCouverture = filtered.length > 0 ? Math.round(stats.fait / filtered.length * 100) : 0;

  async function deleteEntry(id) {
    if (!(await confirmDialog({ title: 'Supprimer', message: 'Supprimer cette séance ?', danger: true }))) return;
    try {
      await api.del(`/registry/${REGISTRY}/${id}`);
      showFlash('Séance supprimée.');
      load();
    } catch (e) { setError(e.message); }
  }

  function getName(arr, id, field = 'name') {
    return arr.find((x) => x.id === id)?.[field] || '—';
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: '1.15rem', fontWeight: 700 }}>Cahier de texte</h1>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            Suivi des cours dispensés et des devoirs donnés
          </p>
        </div>
        <button className="btn btn-sm" onClick={() => setModal('new')}>+ Nouvelle séance</button>
      </div>

      {error && <div className="form-error">{error}</div>}
      {flash && <div className="receipt-banner">{flash}</div>}

      {/* KPIs */}
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 20 }}>
        <div className="kpi"><span>Séances sur la période</span><strong>{filtered.length}</strong></div>
        <div className="kpi green"><span>Faites</span><strong>{stats.fait}</strong></div>
        <div className="kpi gold"><span>Partielles</span><strong>{stats.partiel}</strong></div>
        <div className="kpi red"><span>Reportées</span><strong>{stats.reporte}</strong></div>
      </div>

      {/* Taux couverture */}
      {filtered.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ padding: '12px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Taux de couverture du programme</span>
              <span style={{ fontSize: 13, fontWeight: 800, fontFamily: 'monospace', color: tauxCouverture >= 80 ? 'var(--success)' : tauxCouverture >= 50 ? 'var(--warn)' : 'var(--danger)' }}>
                {tauxCouverture}%
              </span>
            </div>
            <div style={{ height: 10, background: 'var(--line-soft)', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${tauxCouverture}%`, borderRadius: 999, background: tauxCouverture >= 80 ? 'var(--success)' : tauxCouverture >= 50 ? 'var(--warn)' : 'var(--danger)', transition: 'width .4s' }} />
            </div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="card">
        <div className="card-head">
          <h2>Journal des séances</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} style={{ height: 32, fontSize: 12, border: '1px solid var(--line)', borderRadius: 6, padding: '0 8px' }}>
              <option value="">Toutes les classes</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={subjectFilter} onChange={(e) => setSubjectFilter(e.target.value)} style={{ height: 32, fontSize: 12, border: '1px solid var(--line)', borderRadius: 6, padding: '0 8px' }}>
              <option value="">Toutes les matières</option>
              {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ height: 32, fontSize: 12, border: '1px solid var(--line)', borderRadius: 6, padding: '0 8px' }} />
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>→</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ height: 32, fontSize: 12, border: '1px solid var(--line)', borderRadius: 6, padding: '0 8px' }} />
          </div>
        </div>
        <SearchBar value={search} onChange={setSearch} placeholder="Titre, contenu, classe…" total={total} filtered={pgFiltered.length} />
        <div className="panel-body">
          {loading ? <div className="loading">Chargement…</div> :
           pgFiltered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 20px' }}>
              <div style={{ fontSize: '2rem', marginBottom: 10 }}>📔</div>
              <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 14 }}>Aucune séance enregistrée sur cette période.</p>
              <button className="btn" onClick={() => setModal('new')} style={{ width: 'auto', padding: '0 22px' }}>+ Saisir une séance</button>
            </div>
           ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Classe</th>
                    <th>Matière</th>
                    <th>Titre de la séance</th>
                    <th className="num">Durée</th>
                    <th>Statut</th>
                    <th>Devoir</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((e) => {
                    const [badgeClass, badgeLabel] = STATUS[e.status] || ['gray', e.status];
                    return (
                      <tr key={e.id}>
                        <td className="mono" style={{ fontSize: 11 }}>
                          {e.date ? new Date(e.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' }) : '—'}
                        </td>
                        <td style={{ fontSize: 12 }}>{e.className || getName(classes, e.classId)}</td>
                        <td style={{ fontSize: 12 }}>{e.subjectName || getName(subjects, e.subjectId)}</td>
                        <td>
                          <div style={{ fontWeight: 500, fontSize: 12 }}>{e.title || '—'}</div>
                          {e.content && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{e.content.slice(0, 60)}{e.content.length > 60 ? '…' : ''}</div>}
                        </td>
                        <td className="num mono" style={{ fontSize: 12 }}>{e.duration ? `${e.duration}min` : '—'}</td>
                        <td><span className={`badge ${badgeClass}`}>{badgeLabel}</span></td>
                        <td style={{ fontSize: 12, color: e.homework ? 'var(--ink)' : 'var(--muted-light)' }}>
                          {e.homework ? <span title={e.homework}>📝 {e.homework.slice(0, 30)}{e.homework.length > 30 ? '…' : ''}</span> : '—'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <button className="btn-ghost btn-sm" onClick={() => setVue(e)}>Vue</button>
                            <button className="btn-ghost btn-sm" onClick={() => setModal(e)}>Modifier</button>
                            {(!isEnseignant || e.teacherId === user?.id) && (
                              <button className="link-danger" onClick={() => deleteEntry(e.id)}>✕</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination />
            </div>
          )}
        </div>
      </div>

      {vue && (
        <VueSeanceModal entry={vue} classes={classes} subjects={subjects} teachers={teachers}
          onClose={() => setVue(null)}
          onEdit={() => { setModal(vue); setVue(null); }}
        />
      )}

      {modal && (
        <SeanceModal
          initial={modal === 'new' ? EMPTY : modal}
          isNew={modal === 'new'}
          classes={classes} subjects={subjects} teachers={teachers}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); showFlash(modal === 'new' ? 'Séance ajoutée ✓' : 'Séance modifiée ✓'); }}
        />
      )}
    </>
  );
}

// ── Modal Vue Séance (lecture seule) ─────────────────
function VueSeanceModal({ entry: e, classes, subjects, teachers, onClose, onEdit }) {
  function getName(arr, id, fields = ['lastName', 'firstName']) {
    const item = arr.find((x) => x.id === id);
    if (!item) return e.className || e.subjectName || e.teacherName || '—';
    return fields.map((f) => item[f]).filter(Boolean).join(' ') || item.name || '—';
  }

  const STATUS_COLORS = { FAIT: 'green', PARTIEL: 'gold', REPORTE: 'red' };
  const STATUS_LABELS = { FAIT: 'Fait', PARTIEL: 'Partiel', REPORTE: 'Reporté' };
  const badgeClass = STATUS_COLORS[e.status] || 'gray';
  const badgeLabel = STATUS_LABELS[e.status] || e.status;

  const Row = ({ label, value, mono }) => value ? (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted-light)', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--ink)', fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</div>
    </div>
  ) : null;

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(ev) => ev.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <span className={`badge ${badgeClass}`}>{badgeLabel}</span>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                {e.date ? new Date(e.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) : '—'}
              </span>
              {e.duration && <span className="badge gray">{e.duration} min</span>}
            </div>
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--ink)' }}>{e.title || '—'}</h2>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn-ghost btn-sm" onClick={onEdit}>✎ Modifier</button>
            <button className="btn-ghost btn-sm" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Infos principales */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, padding: '12px 14px', background: 'var(--surface-soft)', borderRadius: 10, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted-light)', marginBottom: 3 }}>Classe</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{e.className || getName(classes, e.classId, ['name'])}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted-light)', marginBottom: 3 }}>Matière</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{e.subjectName || getName(subjects, e.subjectId, ['name'])}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted-light)', marginBottom: 3 }}>Enseignant</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{e.teacherName || getName(teachers, e.teacherId)}</div>
          </div>
        </div>

        {/* Contenu */}
        {e.content && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted-light)', marginBottom: 6 }}>Contenu du cours</div>
            <div style={{ fontSize: 13, lineHeight: 1.7, padding: '12px 14px', background: 'var(--surface-soft)', borderRadius: 8, border: '0.5px solid var(--line)', whiteSpace: 'pre-wrap' }}>
              {e.content}
            </div>
          </div>
        )}

        {/* Devoir */}
        {e.homework && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted-light)', marginBottom: 6 }}>📝 Devoir / Travail à faire</div>
            <div style={{ fontSize: 13, lineHeight: 1.7, padding: '12px 14px', background: '#fffbeb', borderRadius: 8, border: '0.5px solid #fde68a', whiteSpace: 'pre-wrap' }}>
              {e.homework}
            </div>
          </div>
        )}

        {!e.content && !e.homework && (
          <div className="empty" style={{ padding: '20px 0' }}>Aucun détail saisi pour cette séance.</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

// ── Modal Séance ──────────────────────────────────────
function SeanceModal({ initial, isNew, classes, subjects, teachers, onClose, onSaved }) {
  const { user } = useAuth();
  const [f, setF] = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  // Auto-remplir le nom de la classe et matière pour la recherche
  useEffect(() => {
    const cls = classes.find((c) => c.id === f.classId);
    const sub = subjects.find((s) => s.id === f.subjectId);
    setF((p) => ({ ...p, className: cls?.name || '', subjectName: sub?.name || '' }));
  }, [f.classId, f.subjectId]);

  async function save() {
    if (!f.classId || !f.subjectId || !f.title) { setError('Classe, matière et titre sont obligatoires.'); return; }
    setSaving(true); setError('');
    try {
      const cls = classes.find((c) => c.id === f.classId);
      const sub = subjects.find((s) => s.id === f.subjectId);
      const tch = teachers.find((t) => t.id === f.teacherId);
      const payload = {
        data: {
          ...f,
          className: cls?.name || '',
          subjectName: sub?.name || '',
          teacherName: tch ? `${tch.lastName} ${tch.firstName}` : '',
          teacherId: f.teacherId || user?.id || '',
        },
        label: `${f.date} · ${cls?.name} · ${sub?.name} · ${f.title}`,
        searchText: `${cls?.name} ${sub?.name} ${f.title} ${f.content}`,
        refDate: f.date,
      };
      if (isNew) {
        await api.post(`/registry/cahier-texte`, payload);
      } else {
        await api.patch(`/registry/cahier-texte/${initial.id}`, payload);
      }
      onSaved();
    } catch (e) { setError(e.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginBottom: 16, fontSize: '1rem', fontWeight: 700 }}>
          {isNew ? 'Nouvelle séance' : 'Modifier la séance'}
        </h2>
        {error && <div className="form-error">{error}</div>}

        <div className="grid-3">
          <div className="field"><label>Date *</label><input type="date" value={f.date} onChange={set('date')} /></div>
          <div className="field"><label>Classe *</label>
            <select value={f.classId} onChange={set('classId')}>
              <option value="">— Sélectionner —</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Matière *</label>
            <select value={f.subjectId} onChange={set('subjectId')}>
              <option value="">— Sélectionner —</option>
              {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>

        <div className="two-col">
          <div className="field"><label>Enseignant</label>
            <select value={f.teacherId} onChange={set('teacherId')}>
              <option value="">— Sélectionner —</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.lastName} {t.firstName}</option>)}
            </select>
          </div>
          <div className="field"><label>Durée (minutes)</label>
            <select value={f.duration} onChange={set('duration')}>
              {[30, 45, 60, 90, 120].map((d) => <option key={d} value={d}>{d} min</option>)}
            </select>
          </div>
        </div>

        <div className="field"><label>Titre de la séance *</label>
          <input value={f.title} onChange={set('title')} placeholder="Ex: Introduction aux fonctions" />
        </div>

        <div className="field"><label>Contenu du cours</label>
          <textarea value={f.content} onChange={set('content')} rows={4}
            placeholder="Résumé des notions abordées, objectifs atteints…" />
        </div>

        <div className="field"><label>Devoir / Travail à faire</label>
          <textarea value={f.homework} onChange={set('homework')} rows={2}
            placeholder="Exercice page 45, lecture du chapitre 3…" />
        </div>

        <div className="field"><label>Statut de la séance</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {Object.entries(STATUS).map(([v, [cls, label]]) => (
              <button key={v} type="button" onClick={() => setF((p) => ({ ...p, status: v }))}
                style={{
                  flex: 1, padding: '8px', borderRadius: 8, border: '1.5px solid',
                  cursor: 'pointer', fontSize: 12, fontWeight: 500,
                  borderColor: f.status === v ? (cls === 'green' ? 'var(--success)' : cls === 'gold' ? 'var(--warn)' : 'var(--danger)') : 'var(--line)',
                  background: f.status === v ? (cls === 'green' ? 'var(--success-soft)' : cls === 'gold' ? 'var(--warn-soft)' : 'var(--danger-soft)') : 'var(--surface)',
                  color: f.status === v ? (cls === 'green' ? 'var(--success)' : cls === 'gold' ? 'var(--warn)' : 'var(--danger)') : 'var(--muted)',
                }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>
            {saving ? 'Enregistrement…' : isNew ? 'Ajouter la séance' : 'Enregistrer'}
          </button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}
